import { useEffect, useState } from "react";
import axios from "axios";
import { LogOut, Scissors } from "lucide-react";
import { OrdersTab } from "../admin/OrdersTab";
import { ProductsTab } from "../admin/ProductsTab";
import { SettingsTab } from "../admin/SettingsTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("innus-admin-token")}`,
});

const inputCls =
  "w-full bg-transparent border-b border-[#E8E4DF] focus:border-[#C5A059] px-0 py-3 text-[#2D1B36] placeholder-[#8A818F] transition-colors duration-300";

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("innus-admin-token"));
  const [checking, setChecking] = useState(!!token);
  const [tab, setTab] = useState("orders");
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setChecking(false);
      return;
    }
    axios
      .get(`${API}/admin/orders`, { headers: authHeaders() })
      .catch(() => {
        localStorage.removeItem("innus-admin-token");
        setToken(null);
      })
      .finally(() => setChecking(false));
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, form);
      localStorage.setItem("innus-admin-token", res.data.token);
      setToken(res.data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("innus-admin-token");
    setToken(null);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center">
        <p className="font-serif-d italic text-2xl text-[#8A818F]">Opening the ledger…</p>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-[#2D1B36] flex items-center justify-center px-6" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm bg-[#FBF9F6] p-10" data-testid="admin-login-form">
          <div className="flex items-center gap-3 mb-2">
            <Scissors size={20} strokeWidth={1.5} className="text-[#C5A059]" />
            <h1 className="font-serif-d text-3xl text-[#2D1B36]">Owner's Desk</h1>
          </div>
          <p className="font-accent text-[10px] tracking-[0.3em] uppercase text-[#8A818F] mb-10">
            Innus Lace Centre — Private
          </p>
          <div className="space-y-7">
            <div>
              <label className="font-accent text-[10px] tracking-[0.3em] uppercase text-[#8A818F]">Email</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="owner@innuslace.com"
                data-testid="admin-login-email"
                className={inputCls}
              />
            </div>
            <div>
              <label className="font-accent text-[10px] tracking-[0.3em] uppercase text-[#8A818F]">Password</label>
              <input
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
                data-testid="admin-login-password"
                className={inputCls}
              />
            </div>
          </div>
          {error && (
            <p data-testid="admin-login-error" className="mt-6 text-sm text-red-700">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="mt-10 w-full bg-[#4A2B58] text-[#FBF9F6] font-accent text-xs tracking-[0.25em] uppercase py-4 hover:bg-[#2D1B36] transition-colors duration-300 disabled:opacity-60"
          >
            {busy ? "Signing in…" : "Sign In"}
          </button>
          <a
            href="/"
            data-testid="admin-back-to-store"
            className="block mt-6 text-center font-accent text-[10px] tracking-[0.25em] uppercase text-[#8A818F] hover:text-[#C5A059] transition-colors"
          >
            Back to store
          </a>
        </form>
      </div>
    );
  }

  const tabs = [
    { id: "orders", label: "Orders" },
    { id: "products", label: "Products & Prices" },
    { id: "settings", label: "Storefront" },
  ];

  return (
    <div className="min-h-screen bg-[#F2EFEB]" data-testid="admin-dashboard">
      <header className="bg-[#2D1B36] text-[#FBF9F6]">
        <div className="max-w-[1100px] mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <p className="font-serif-d text-2xl">
              Innus <span className="italic text-[#C5A059]">Lace Centre</span>
            </p>
            <p className="font-accent text-[9px] tracking-[0.3em] uppercase text-[#FBF9F6]/50 mt-1">
              Owner's Desk
            </p>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="/"
              data-testid="admin-view-store"
              className="font-accent text-[10px] tracking-[0.25em] uppercase text-[#FBF9F6]/60 hover:text-[#DFCA9B] transition-colors"
            >
              View Store
            </a>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 border border-[#FBF9F6]/30 px-4 py-2 font-accent text-[10px] tracking-[0.25em] uppercase hover:bg-[#FBF9F6]/10 transition-colors"
            >
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>
        <div className="max-w-[1100px] mx-auto px-6 flex gap-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`font-accent text-[11px] tracking-[0.2em] uppercase px-5 py-3.5 transition-colors duration-300 ${
                tab === t.id
                  ? "bg-[#F2EFEB] text-[#2D1B36]"
                  : "text-[#FBF9F6]/60 hover:text-[#DFCA9B]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>
      <main className="max-w-[1100px] mx-auto px-6 py-10">
        {tab === "orders" && <OrdersTab />}
        {tab === "products" && <ProductsTab />}
        {tab === "settings" && <SettingsTab />}
      </main>
    </div>
  );
}
